import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { formatCompactOrIsoDate, formatCurrencyAmount } from '../../shared/trade-formatters';
import { parseInvoiceLineAmount } from '../invoice-line-amount';
import { InvoicesService } from '../invoices.service';
import { RelatedRecordsSelector } from '../related-records-selector/related-records-selector';
import {
  BondDocumentationOption,
  CreateInvoiceInput,
  FrequencyOption,
  InvoiceLineDraft,
  InvoiceLineInput,
  InvoiceLinkType,
  InvoiceLinkTypeCode,
  RelatedRecordLinkTypeCode,
  RelatedRecordSelection,
  InvoiceSummary,
  InvoiceTypeOption,
  Vendor,
} from '../invoices.types';

interface InvoiceEditFormState {
  vendorId: number | null;
  linkType: InvoiceLinkType;
  linkedBondDocumentationId: number | null;
  issueDate: string;
  invoiceNumber: string;
  serviceDateStart: string;
  serviceDateEnd: string;
  currency: string;
  frequencyCode: string;
  recipientEmail: string;
  amountText: string | number;
}

@Component({
  selector: 'app-invoice-detail',
  imports: [CommonModule, FormsModule, RouterLink, RelatedRecordsSelector],
  templateUrl: './invoice-detail.html',
  styleUrls: ['../invoice-sheet.css', './invoice-detail.css'],
})
export class InvoiceDetail implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly invoicesService = inject(InvoicesService);

  protected readonly invoice = signal<InvoiceSummary | null>(null);
  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly invoiceTypeOptions = signal<InvoiceTypeOption[]>([]);
  protected readonly frequencyOptions = signal<FrequencyOption[]>([]);
  protected readonly bondDocumentationOptions = signal<BondDocumentationOption[]>([]);

  protected readonly isEditing = signal(false);
  protected readonly isLoadingEditOptions = signal(false);
  protected readonly hasLoadedEditOptions = signal(false);
  protected readonly isSaving = signal(false);
  protected readonly savingAction = signal<'email' | 'save' | 'save-send' | null>(null);
  protected readonly validationMessage = signal('');
  protected readonly attachmentName = signal<string>('');
  protected readonly attachmentFile = signal<File | null>(null);
  protected readonly loadError = signal('');

  protected readonly lineDrafts = signal<InvoiceLineDraft[]>([]);
  protected readonly isLinkModalOpen = signal(false);

  protected readonly form = signal<InvoiceEditFormState>({
    vendorId: null,
    linkType: 'Generic',
    linkedBondDocumentationId: null,
    issueDate: '',
    invoiceNumber: '',
    serviceDateStart: '',
    serviceDateEnd: '',
    currency: 'USD',
    frequencyCode: '',
    recipientEmail: '',
    amountText: '',
  });

  protected readonly selectedVendor = computed(() => {
    const selectedVendorId = this.form().vendorId;
    return this.vendors().find((vendor) => vendor.id === selectedVendorId);
  });

  protected readonly availableInvoiceConceptOptions = computed(() => {
    const selected = this.selectedVendor();
    if (selected?.emittedInvoiceConceptIds.length) {
      const allowed = new Set(selected.emittedInvoiceConceptIds);
      const inUse = new Set(this.lineDrafts().map((line) => line.invConceptId).filter((id): id is number => !!id));
      const fromVendor = this.invoiceTypeOptions().filter((item) => allowed.has(item.id));
      // Concepts already saved on a line stay pickable even if the vendor no longer emits them.
      const alreadyUsed = this.invoiceTypeOptions().filter((item) => inUse.has(item.id) && !allowed.has(item.id));

      return [...fromVendor, ...alreadyUsed];
    }

    return this.invoiceTypeOptions();
  });

  protected readonly selectedConceptNames = computed(() => {
    const inUse = new Set(this.lineDrafts().map((line) => line.invConceptId).filter((id): id is number => !!id));
    return this.invoiceTypeOptions().filter((option) => inUse.has(option.id)).map((option) => option.name);
  });

  protected readonly lineTotal = computed(() => {
    return this.lineDrafts().reduce((sum, line) => {
      const value = Number(line.amountText);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
  });

  protected readonly enteredInvoiceTotal = computed(() => {
    const rawAmount = this.toText(this.form().amountText).trim();
    if (!rawAmount) {
      return this.lineTotal();
    }

    const value = Number(rawAmount);
    return Number.isFinite(value) ? value : 0;
  });

  protected readonly linkSelectionSummary = computed(() => {
    const linked = this.lineDrafts().filter((line) => line.linkTypeCode !== 'NONE');
    const types = new Set(linked.map((line) => line.linkTypeCode));
    if (types.size > 1) {
      return `${linked.length} linked records · ${this.formatLinkTypes(types)}`;
    }
    return linked.length
      ? `${linked.length} linked records · ${this.formatLinkTypes(types)}`
      : 'No linked records selected';
  });

  protected readonly selectedLinkTypeCode = computed<RelatedRecordLinkTypeCode>(() => {
    return this.toLinkTypeCode(this.form().linkType) as RelatedRecordLinkTypeCode;
  });

  protected readonly modalInitialSelections = computed<RelatedRecordSelection[]>(() => {
    const mode = this.selectedLinkTypeCode();
    const seen = new Set<string>();
    return this.lineDrafts()
      .filter((line) => this.isLineInCurrentSelectionScope(line) && !!line.tradeId)
      .filter((line) => !seen.has(line.tradeId) && !!seen.add(line.tradeId))
      .map((line) => ({
        record: {
          linkTypeCode: mode,
          tradeId: line.tradeId,
          dealId: line.dealId,
          securityId: line.securityId,
          bondDocumentationId: line.bondDocumentationId,
          bondDocumentationName: line.bondDocumentationName,
          seriesNumber: line.seriesNumber,
          marketType: line.marketType,
          securityType: '',
          currency: line.tradeCcy,
          tradeDate: '',
          settlementDate: '',
          maturityDate: '',
          notional: 0,
          cusip: '',
          isin: '',
          tranche: '',
        },
        amountText: line.amountText,
      }));
  });

  protected readonly invoiceLinkSummary = computed(() => {
    const types = new Set((this.invoice()?.lines ?? []).map((line) => line.linkTypeCode));
    if (types.size > 1) {
      return this.formatLinkTypes(types);
    }
    const type = [...types][0] ?? 'NONE';
    return this.linkTypeLabel(type);
  });

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const invoiceId = Number(params.get('invoiceId'));
      if (!Number.isFinite(invoiceId) || invoiceId <= 0) {
        this.invoice.set(null);
        this.loadError.set('Invoice not found.');
        return;
      }

      this.invoicesService.getInvoiceById(invoiceId).subscribe({
        next: (invoice) => {
          this.invoice.set(invoice ?? null);
          this.loadError.set(invoice ? '' : 'Invoice not found.');

          if (invoice) {
            this.hydrateFormFromInvoice(invoice);
            this.invoicesService.getVendorOptionById(invoice.vendorId).subscribe((vendor) => {
              this.vendors.set(vendor ? [vendor] : []);
            });
          }
        },
        error: () => {
          this.invoice.set(null);
          this.loadError.set('Unable to load invoice detail from the API.');
        },
      });
    });
  }

  protected startEditing(): void {
    const current = this.invoice();
    if (!current || this.isLoadingEditOptions()) {
      return;
    }

    if (this.hasLoadedEditOptions()) {
      this.beginEditing(current);
      return;
    }

    this.isLoadingEditOptions.set(true);
    forkJoin({
      vendors: this.invoicesService.getVendorOptions(),
      conceptOptions: this.invoicesService.getInvoiceTypeOptions(),
      frequencyOptions: this.invoicesService.getFrequencyOptions(),
      bondDocumentationOptions: this.invoicesService.getBondDocumentationOptions(),
      vendorConceptIds: this.invoicesService.getVendorInvoiceConceptIds(current.vendorId),
    }).subscribe({
      next: ({ vendors, conceptOptions, frequencyOptions, bondDocumentationOptions, vendorConceptIds }) => {
        this.vendors.set(vendors.map((vendor) => vendor.id === current.vendorId
          ? { ...vendor, emittedInvoiceConceptIds: vendorConceptIds }
          : vendor));
        this.invoiceTypeOptions.set(conceptOptions);
        this.frequencyOptions.set(frequencyOptions);
        this.bondDocumentationOptions.set(bondDocumentationOptions);
        this.hasLoadedEditOptions.set(true);
        this.isLoadingEditOptions.set(false);
        this.beginEditing(current);
      },
      error: () => {
        this.isLoadingEditOptions.set(false);
        this.validationMessage.set('Unable to load invoice editing options.');
      },
    });
  }

  protected cancelEditing(): void {
    const current = this.invoice();
    if (current) {
      this.hydrateFormFromInvoice(current);
    }

    this.validationMessage.set('');
    this.isLinkModalOpen.set(false);
    this.isEditing.set(false);
  }

  protected onVendorChange(vendorIdRaw: string): void {
    const vendorId = Number(vendorIdRaw);
    const selected = this.vendors().find((vendor) => vendor.id === vendorId);
    const current = this.form();

    this.form.set({
      ...current,
      vendorId: Number.isFinite(vendorId) ? vendorId : null,
      currency: selected?.defaultCurrency ?? current.currency,
    });

    if (selected && selected.emittedInvoiceConceptIds.length === 0) {
      this.invoicesService.getVendorInvoiceConceptIds(selected.id).subscribe((conceptIds) => {
        this.vendors.update((vendors) => vendors.map((vendor) => vendor.id === selected.id
          ? { ...vendor, emittedInvoiceConceptIds: conceptIds }
          : vendor));
      });
    }
  }

  private beginEditing(invoice: InvoiceSummary): void {
    this.hydrateFormFromInvoice(invoice);
    this.validationMessage.set('');
    this.isEditing.set(true);
  }

  protected onLinkTypeChange(linkType: InvoiceLinkType): void {
    const current = this.form();
    this.form.set({
      ...current,
      linkType,
      linkedBondDocumentationId: linkType === 'Program' ? current.linkedBondDocumentationId : null,
    });

    if (linkType === 'Program' && !this.form().linkedBondDocumentationId) {
      return;
    }

    this.openLinkModal();
  }

  protected onProgramChange(bondDocumentationId: number | null): void {
    this.onFieldChange('linkedBondDocumentationId', bondDocumentationId);
    if (bondDocumentationId) {
      this.openLinkModal();
    }
  }

  protected addGenericLine(): void {
    this.lineDrafts.set([...this.lineDrafts(), this.createLineDraft()]);
  }

  protected duplicateLine(key: string): void {
    const source = this.lineDrafts().find((line) => line.key === key);
    if (!source) {
      return;
    }

    const clone: InvoiceLineDraft = {
      ...source,
      key: `line-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      invConceptId: null,
      detail: '',
    };
    const index = this.lineDrafts().findIndex((line) => line.key === key);
    const next = [...this.lineDrafts()];
    next.splice(index + 1, 0, clone);
    this.lineDrafts.set(next);
  }

  protected removeLine(key: string): void {
    this.lineDrafts.set(this.lineDrafts().filter((line) => line.key !== key));
  }

  protected onLineFieldChange<K extends keyof InvoiceLineDraft>(key: string, field: K, value: InvoiceLineDraft[K]): void {
    this.lineDrafts.set(this.lineDrafts().map((line) => (line.key === key ? { ...line, [field]: value } : line)));
  }

  protected onFieldChange<K extends keyof InvoiceEditFormState>(key: K, value: InvoiceEditFormState[K]): void {
    const current = this.form();
    this.form.set({ ...current, [key]: value });
  }

  protected openLinkModal(): void {
    if (this.form().linkType === 'Generic') {
      this.validationMessage.set('Choose Deal ID, Security ID, or Program before adding linked records.');
      return;
    }
    if (this.form().linkType === 'Program' && !this.form().linkedBondDocumentationId) {
      this.validationMessage.set('Select a program before adding linked records.');
      return;
    }

    this.validationMessage.set('');
    this.isLinkModalOpen.set(true);
  }

  protected closeLinkModal(): void {
    this.isLinkModalOpen.set(false);
  }

  protected saveLinkModalSelection(selections: RelatedRecordSelection[]): void {
    const selectedTradeIds = new Set(selections.map(({ record }) => record.tradeId));
    const existing = this.lineDrafts();
    const defaultConceptId = this.availableInvoiceConceptOptions()[0]?.id ?? null;
    const byTradeId = new Map(selections.map((selection) => [selection.record.tradeId, selection]));
    const kept = existing
      .filter((line) => !this.isLineInCurrentSelectionScope(line) || selectedTradeIds.has(line.tradeId))
      .map((line) => {
        const selection = this.isLineInCurrentSelectionScope(line) ? byTradeId.get(line.tradeId) : undefined;
        return selection ? this.applySelectionToLine(line, selection) : line;
      });
    const added = selections
      .filter(({ record }) => !existing.some((line) => this.isLineInCurrentSelectionScope(line) && line.tradeId === record.tradeId))
      .map((selection) => ({
        ...this.createLineDraft(selection),
        invConceptId: defaultConceptId,
      }));

    this.lineDrafts.set([...kept, ...added]);
    this.closeLinkModal();
  }

  protected getCommittedAmountForTrade(tradeId: string): number {
    return this.lineDrafts()
      .filter((line) => line.tradeId === tradeId)
      .reduce((sum, line) => {
        const value = Number(line.amountText);
        return Number.isFinite(value) ? sum + value : sum;
      }, 0);
  }

  protected onAttachmentSelected(files: FileList | null): void {
    const firstFile = files?.item(0) ?? null;
    this.attachmentFile.set(firstFile);
    this.attachmentName.set(firstFile?.name ?? '');
  }

  protected clearAttachment(): void {
    this.attachmentFile.set(null);
    this.attachmentName.set('');
  }

  protected saveInvoiceEdits(): void {
    this.saveInvoice(false);
  }

  protected saveAndSendEmail(): void {
    this.saveInvoice(true);
  }

  protected sendEmailOnly(): void {
    if (this.isSaving()) {
      return;
    }

    const currentInvoice = this.invoice();
    const invoiceCode = this.isEditing() ? this.form().invoiceNumber.trim() : (currentInvoice?.invoiceNumber ?? '');
    if (!invoiceCode) {
      this.validationMessage.set('Invoice number is required to send the approval email.');
      return;
    }

    const confirmed = window.confirm(`Send approval email for invoice "${invoiceCode}" now?`);
    if (!confirmed) {
      return;
    }

    this.isSaving.set(true);
    this.savingAction.set('email');
    this.validationMessage.set('');
    this.invoicesService.sendInvoiceApprovalEmail({
      invoiceCode,
      recipientEmail: this.form().recipientEmail || null,
      messageDetail: this.buildApprovalMessage(invoiceCode),
    }).subscribe({
      next: (sent) => {
        this.isSaving.set(false);
        this.savingAction.set(null);
        if (!sent) {
          this.validationMessage.set('Unable to send approval email. Verify the invoice code and document.');
          return;
        }

        window.KTToast?.show({ message: 'Approval email sent successfully.', variant: 'success' });
      },
      error: () => {
        this.isSaving.set(false);
        this.savingAction.set(null);
        this.validationMessage.set('Unable to send approval email.');
      },
    });
  }

  private saveInvoice(sendEmailAfterSave: boolean): void {
    const currentInvoice = this.invoice();
    if (!currentInvoice || this.isSaving()) {
      return;
    }

    const recipientEmail = this.form().recipientEmail;
    const payload = this.buildUpdatePayload();
    if (!payload) {
      return;
    }

    this.isSaving.set(true);
    this.savingAction.set(sendEmailAfterSave ? 'save-send' : 'save');
    this.invoicesService.updateInvoice(currentInvoice.id, payload).subscribe({
      next: ({ result: updated, documentError }) => {
        if (!updated) {
          this.isSaving.set(false);
          this.savingAction.set(null);
          this.validationMessage.set('Unable to update invoice.');
          return;
        }

        if (documentError) {
          window.KTToast?.show({
            message: `Invoice updated, but the new document could not be uploaded. ${documentError}`,
            variant: 'warning',
          });
        }

        this.invoicesService.getInvoiceById(currentInvoice.id).subscribe({
          next: (refreshed) => {
            if (refreshed) {
              this.invoice.set(refreshed);
              this.hydrateFormFromInvoice(refreshed);
            }

            if (!sendEmailAfterSave) {
              this.isSaving.set(false);
              this.savingAction.set(null);
              this.isEditing.set(false);
              if (!documentError) {
                window.KTToast?.show({ message: 'Invoice updated successfully.', variant: 'success' });
              }
              return;
            }

            const invoiceCode = payload.invoiceNumber;
            this.invoicesService.sendInvoiceApprovalEmail({
              invoiceCode,
              recipientEmail: recipientEmail || null,
              messageDetail: this.buildApprovalMessage(invoiceCode),
            }).subscribe({
              next: (sent) => {
                this.isSaving.set(false);
                this.savingAction.set(null);
                if (!sent) {
                  this.validationMessage.set('Invoice was updated, but approval email could not be sent.');
                  return;
                }

                this.isEditing.set(false);
                window.KTToast?.show({ message: 'Invoice updated and approval email sent.', variant: 'success' });
              },
              error: () => {
                this.isSaving.set(false);
                this.savingAction.set(null);
                this.validationMessage.set('Invoice was updated, but approval email could not be sent.');
              },
            });
          },
          error: () => {
            this.isSaving.set(false);
            this.savingAction.set(null);
            this.validationMessage.set('Unable to refresh invoice after update.');
          },
        });
      },
      error: () => {
        this.isSaving.set(false);
        this.savingAction.set(null);
        this.validationMessage.set('Unable to update invoice.');
      },
    });
  }

  protected deleteInvoice(): void {
    const currentInvoice = this.invoice();
    if (!currentInvoice) {
      return;
    }

    const confirmed = window.confirm(`Delete invoice "${currentInvoice.invoiceNumber}"?`);
    if (!confirmed) {
      return;
    }

    this.invoicesService.deleteInvoice(currentInvoice.id).subscribe({
      next: (result) => {
        if (!result.deleted) {
          window.KTToast?.show({ message: result.message || 'Unable to delete invoice.', variant: 'destructive' });
          return;
        }

        window.KTToast?.show({ message: 'Invoice deleted successfully.', variant: 'success' });
        void this.router.navigate(['/invoices']);
      },
      error: () => {
        window.KTToast?.show({ message: 'Unable to delete invoice.', variant: 'destructive' });
      },
    });
  }

  protected formatDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    return formatCompactOrIsoDate(value);
  }

  protected formatAmount(currency: string, amount: number | null): string {
    if (amount === null) {
      return '-';
    }

    return formatCurrencyAmount(currency, amount);
  }

  protected formatLinkDisplay(): string {
    const current = this.form();
    if (current.linkType === 'Program' && current.linkedBondDocumentationId) {
      const selected = this.bondDocumentationOptions().find((item) => item.id === current.linkedBondDocumentationId);
      return selected ? `${current.linkType} (${selected.name})` : current.linkType;
    }

    return current.linkType;
  }

  private hydrateFormFromInvoice(invoice: InvoiceSummary): void {
    const preferredFrequency = invoice.frequencyCode
      || this.frequencyOptions().find((option) => option.name === invoice.frequency)?.code
      || '';

    this.form.set({
      vendorId: invoice.vendorId,
      linkType: 'Generic',
      linkedBondDocumentationId: invoice.lines.find((line) => line.bondDocumentationId)?.bondDocumentationId
        ?? invoice.linkedBondDocumentationId,
      issueDate: invoice.issueDate,
      invoiceNumber: invoice.invoiceNumber,
      serviceDateStart: invoice.serviceDateStart ?? '',
      serviceDateEnd: invoice.serviceDateEnd ?? '',
      currency: invoice.currency,
      frequencyCode: preferredFrequency,
      recipientEmail: '',
      amountText: invoice.amount,
    });

    this.lineDrafts.set(invoice.lines.map((line, index) => ({
      key: `line-${line.id ?? index}-${index}`,
      invConceptId: line.invConceptId || null,
      linkTypeCode: line.linkTypeCode,
      tradeId: line.tradeId ?? '',
      dealId: line.dealId ?? '',
      securityId: line.securityId ?? '',
      bondDocumentationId: line.bondDocumentationId,
      bondDocumentationName: line.bondDocumentationName,
      tradeCcy: line.tradeCcy,
      seriesNumber: '',
      marketType: '',
      amountText: `${line.lineAmount}`,
      detail: line.detail,
    })));
    this.attachmentFile.set(null);
    this.attachmentName.set('');
  }

  /** Mirrors the backend line rules before the replace call goes out. */
  private validateLines(): boolean {
    const lines = this.lineDrafts();
    if (lines.length === 0) {
      this.validationMessage.set('Add at least one invoice line.');
      return false;
    }

    const seen = new Set<string>();

    for (const line of lines) {
      if (!line.invConceptId) {
        this.validationMessage.set('Every invoice line needs a concept.');
        return false;
      }

      if (parseInvoiceLineAmount(line.amountText) === null) {
        this.validationMessage.set('Every invoice line needs a valid non-negative amount.');
        return false;
      }

      if (line.linkTypeCode !== 'NONE' && !line.tradeId) {
        this.validationMessage.set('Every invoice line needs a linked trade for this reference type.');
        return false;
      }
      if (line.linkTypeCode === 'DEAL' && !line.dealId) {
        this.validationMessage.set('Every Deal ID line needs a deal reference.');
        return false;
      }
      if (line.linkTypeCode === 'SECURITY' && !line.securityId) {
        this.validationMessage.set('Every Security ID line needs a security reference.');
        return false;
      }
      if (line.linkTypeCode === 'BOND_DOCUMENTATION' && !line.bondDocumentationId) {
        this.validationMessage.set('Every Program line needs a program reference.');
        return false;
      }

      const signature = `${line.invConceptId}|${line.linkTypeCode}|${line.tradeId}|${line.dealId}|${line.securityId}|${line.bondDocumentationId ?? ''}`;
      if (seen.has(signature)) {
        this.validationMessage.set('The same concept cannot repeat on the same linked record.');
        return false;
      }
      seen.add(signature);
    }

    return true;
  }

  private createLineDraft(selection?: RelatedRecordSelection): InvoiceLineDraft {
    const record = selection?.record;
    return {
      key: `line-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
      invConceptId: this.availableInvoiceConceptOptions()[0]?.id ?? null,
      linkTypeCode: record?.linkTypeCode ?? 'NONE',
      tradeId: record?.tradeId ?? '',
      dealId: record?.dealId ?? '',
      securityId: record?.securityId ?? '',
      bondDocumentationId: record?.bondDocumentationId ?? null,
      bondDocumentationName: record?.bondDocumentationName ?? '',
      tradeCcy: record?.currency ?? '',
      seriesNumber: record?.seriesNumber ?? '',
      marketType: record?.marketType ?? '',
      amountText: selection?.amountText ?? '',
      detail: '',
    };
  }

  private applySelectionToLine(line: InvoiceLineDraft, selection: RelatedRecordSelection): InvoiceLineDraft {
    const record = selection.record;
    const matchingLineCount = this.lineDrafts().filter((candidate) => (
      this.isLineInCurrentSelectionScope(candidate) && candidate.tradeId === record.tradeId
    )).length;
    return {
      ...line,
      tradeId: record.tradeId,
      dealId: record.dealId,
      securityId: record.securityId,
      bondDocumentationId: record.bondDocumentationId,
      bondDocumentationName: record.bondDocumentationName,
      tradeCcy: record.currency,
      seriesNumber: record.seriesNumber,
      marketType: record.marketType,
      amountText: matchingLineCount === 1 ? selection.amountText : line.amountText,
    };
  }

  private isLineInCurrentSelectionScope(line: InvoiceLineDraft): boolean {
    const mode = this.selectedLinkTypeCode();
    return line.linkTypeCode === mode
      && (mode !== 'BOND_DOCUMENTATION' || line.bondDocumentationId === this.form().linkedBondDocumentationId);
  }

  private toLinkTypeCode(linkType: InvoiceLinkType): InvoiceLinkTypeCode {
    switch (linkType) {
      case 'Deal ID':
        return 'DEAL';
      case 'Security ID':
        return 'SECURITY';
      case 'Program':
        return 'BOND_DOCUMENTATION';
      default:
        return 'NONE';
    }
  }

  private buildLinePayload(): InvoiceLineInput[] {
    return this.lineDrafts().map((line) => {
      return {
        invConceptId: line.invConceptId ?? 0,
        linkTypeCode: line.linkTypeCode,
        tradeId: line.linkTypeCode === 'NONE' ? null : line.tradeId,
        dealId: line.linkTypeCode === 'DEAL' ? line.dealId : null,
        securityId: line.linkTypeCode === 'SECURITY' ? line.securityId : null,
        bondDocumentationId: line.linkTypeCode === 'BOND_DOCUMENTATION' ? line.bondDocumentationId : null,
        tradeCcy: line.linkTypeCode === 'NONE' ? null : line.tradeCcy,
        lineAmount: Number(line.amountText) || 0,
        detail: line.detail.trim() || null,
      };
    });
  }

  private buildUpdatePayload(): CreateInvoiceInput | null {
    this.validationMessage.set('');
    const current = this.form();

    if (!current.vendorId || !current.invoiceNumber.trim() || !current.issueDate) {
      this.validationMessage.set('Vendor, invoice number, and issue date are required.');
      return null;
    }

    // An invoice must always end up with a document: either one is already stored or a new
    // file is being uploaded with this edit.
    if (!this.attachmentFile() && (this.invoice()?.documents.length ?? 0) === 0) {
      this.validationMessage.set('Attach the invoice document before saving.');
      return null;
    }

    const amount = this.enteredInvoiceTotal();
    if (!Number.isFinite(amount) || amount < 0) {
      this.validationMessage.set('Invoice total amount must be a valid non-negative number.');
      return null;
    }

    if (!this.validateLines()) {
      return null;
    }

    return {
      vendorId: current.vendorId,
      issueDate: current.issueDate,
      linkType: this.headerLinkType(),
      invoiceNumber: current.invoiceNumber.trim(),
      serviceDateStart: current.serviceDateStart || null,
      serviceDateEnd: current.serviceDateEnd || null,
      currency: current.currency.trim().toUpperCase(),
      frequencyCode: current.frequencyCode || null,
      amount,
      attachmentName: this.attachmentName() || null,
      attachmentFile: this.attachmentFile(),
      lines: this.buildLinePayload(),
    };
  }

  private toText(value: string | number): string {
    return typeof value === 'number' ? `${value}` : value;
  }

  private buildApprovalMessage(invoiceCode: string): string {
    return `This invoice is approved. Invoice code: ${invoiceCode}.`;
  }

  protected linkTypeLabel(linkTypeCode: InvoiceLinkTypeCode): string {
    switch (linkTypeCode) {
      case 'DEAL':
        return 'Deal ID';
      case 'SECURITY':
        return 'Security ID';
      case 'BOND_DOCUMENTATION':
        return 'Program';
      default:
        return 'Generic';
    }
  }

  private toUiLinkType(linkTypeCode: InvoiceLinkTypeCode): InvoiceLinkType {
    return this.linkTypeLabel(linkTypeCode) as InvoiceLinkType;
  }

  private headerLinkType(): InvoiceLinkType {
    const linkedTypes = new Set(this.lineDrafts().map((line) => line.linkTypeCode));
    return linkedTypes.size === 1 ? this.toUiLinkType([...linkedTypes][0]) : 'Generic';
  }

  private formatLinkTypes(types: Set<InvoiceLinkTypeCode>): string {
    const order: InvoiceLinkTypeCode[] = ['NONE', 'DEAL', 'SECURITY', 'BOND_DOCUMENTATION'];
    return order.filter((type) => types.has(type)).map((type) => this.linkTypeLabel(type)).join(' + ');
  }
}
